import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Breadcrumbs,
  Container,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Link,
  Typography,
  TextField,
} from "@mui/material";
import bkstApi from "../../api";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import { Link as RouterLink } from "react-router-dom";
import StandardItem from "./StandardItem";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import TagOutlinedIcon from "@mui/icons-material/TagOutlined";
import CustomItem from "./CustomItem";
import {
  anyWordStartsWith,
  capitalize,
  getDataFromLocalStorage,
  setDataInLocalStorage,
} from "../../common/util";
import {
  readPosQuickPicks,
  recordCategorySelection,
  recordProductSelection,
} from "../../common/recentSelections";
import Option, { OPTION_GRID_TILE_SX } from "./Option";
import OptionGridCell from "./OptionGridCell";
import { SearchOutlined } from "@mui/icons-material";

const CONTAIN = {
  minWidth: 0,
  maxWidth: "100%",
  width: "100%",
  boxSizing: "border-box",
};

/** MUI Grid spacing — shared so header and catalog columns align. */
const GRID_COLUMN_SPACING = 2;
const GRID_ROW_SPACING = 2;

const catalogGridContainerSx = {
  alignItems: "stretch",
  marginTop: 0,
};

const RECENT_LABEL_MAX = 18;

const QUICK_ICON_SX = { fontSize: "1.1rem" };

const QUICK_TYPE_ICONS = {
  category: <FolderOutlinedIcon sx={QUICK_ICON_SX} />,
  item: <TagOutlinedIcon sx={QUICK_ICON_SX} />,
};

const QUICK_TILE_SX = {
  ...OPTION_GRID_TILE_SX,
  backgroundColor: "#FFFFFF",
  border: "1px solid #C5C5C5",
  boxShadow: "0 1px 2px rgba(0, 0, 0, 0.06)",
  "& > .MuiBox-root": {
    px: 1.25,
  },
  "& .MuiTypography-body1": {
    fontSize: "0.875rem",
    lineHeight: 1.2,
  },
  "& .MuiTypography-caption": {
    color: "text.secondary",
    fontWeight: 600,
    textTransform: "uppercase",
    fontSize: "0.65rem",
    letterSpacing: "0.04em",
    mt: 0.25,
  },
};

function truncateQuickLabel(s, max = RECENT_LABEL_MAX) {
  if (!s) return "";
  const t = s.trim();
  if (t.length <= max) return t;
  return `${t.slice(0, max - 1)}…`;
}

export default function AllItems(props) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("");
  const [quickPicks, setQuickPicks] = useState(() => readPosQuickPicks());

  const refreshQuickPicks = () => setQuickPicks(readPosQuickPicks());

  const [products, setProducts] = useState([]);
  const [categoryMap, setCategoryMap] = useState("");

  const [loading, setLoading] = useState(true);

  const scrollToCategory = (cat) => {
    const el = document.getElementById(cat);
    if (el) {
      el.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  };

  useEffect(() => {
    setLoading(true);

    const cache = getDataFromLocalStorage("products", 5 * 60 * 1000);
    // 5 minutes
    if (cache) {
      const products = cache.items.sort((o1, o2) => o1.title.localeCompare(o2.title));

      setProducts(products);

      setCategoryMap(cache.categories);
      console.log("Cache found");
    } else {
      bkstApi(`/my/product?filter=active&channel=pos`).then((res) => {
        /*let sortedByRank = res.data.products.sort((o1, o2) => {
          let rslt = (+o2.rank || 0) - (+o1.rank || 0);
          if (rslt === 0) {
            rslt = o1.title > o2.title ? 1 : -1;
          }
          return rslt;
        });*/
        const products = res.data.products.sort((o1, o2) =>
          o1.title.trim().localeCompare(o2.title.trim())
        );

        setProducts(products);

        let map = {};
        for (const p of products) {
          const cats = p.category || [p.type.toLowerCase()];
          for (let cat of cats) {
            cat = cat.toLowerCase();
            const arr = map[cat] || [];
            arr.push(p);
            map[cat] = arr;
          }
        }
        setCategoryMap(map);

        setLoading(false);
        setDataInLocalStorage("products", { items: products, categories: map });
      });
    }
  }, []);

  const filteredCategories = useMemo(() => {
    if (!searchQuery || selectedCategory || !categoryMap || typeof categoryMap !== "object") {
      return [];
    }
    return Object.entries(categoryMap)
      .filter(([cat]) => anyWordStartsWith(capitalize(cat), searchQuery))
      .sort(([k1], [k2]) => k1.trim().localeCompare(k2.trim()));
  }, [searchQuery, selectedCategory, categoryMap]);

  const filteredProducts = useMemo(() => {
    if (selectedCategory) {
      const items = categoryMap?.[selectedCategory] || [];
      return [...items].sort((o1, o2) => o1.title.trim().localeCompare(o2.title.trim()));
    }
    if (!searchQuery) {
      return [];
    }
    return products
      .filter((o) => anyWordStartsWith(o.title, searchQuery))
      .sort((o1, o2) => o1.title.trim().localeCompare(o2.title.trim()));
  }, [products, searchQuery, selectedCategory, categoryMap]);

  useEffect(() => {
    if (!selectedCategory && !searchQuery) {
      return;
    }
    scrollToCategory("product-list");
  }, [selectedCategory, searchQuery]);

  const scrollPosToTop = () => {
    window.scrollTo({ top: 0, behavior: "auto" });
  };

  const handleItemChange = (item) => {
    setSearchQuery("");
    setSelectedCategory("");
    if (item && typeof item === "object") {
      recordProductSelection(item, null);
      refreshQuickPicks();
      scrollPosToTop();
    }
    props.onChange(item);
  };

  const applyCategoryFilter = (categoryKey) => {
    const key = categoryKey.toLowerCase().trim();
    recordCategorySelection(key);
    refreshQuickPicks();
    setSelectedCategory(key);
    setSearchQuery("");
  };

  const clearFilters = () => {
    setSearchQuery("");
    setSelectedCategory("");
  };

  const { popularCategories, popularProducts, recentCategory, recentProduct } = quickPicks;
  const quickPickTiles = [];

  (popularCategories || []).forEach((entry, idx) => {
    quickPickTiles.push({
      key: `popular-cat-${idx}-${entry.label}`,
      kind: "category",
      title: entry.label,
      titleIcon: QUICK_TYPE_ICONS.category,
      description: "Popular",
      ariaLabel: `${entry.label}, popular category`,
      onClick: () => applyCategoryFilter(entry.label),
    });
  });

  (popularProducts || []).forEach((entry) => {
    quickPickTiles.push({
      key: `popular-prod-${entry.id}`,
      kind: "product",
      title: entry.title,
      titleIcon: QUICK_TYPE_ICONS.item,
      description: "Popular",
      ariaLabel: `${entry.title}, popular item`,
      onClick: () => {
        setSelectedCategory("");
        setSearchQuery(entry.title);
      },
    });
  });

  if (recentCategory?.label) {
    quickPickTiles.push({
      key: "recent-cat",
      kind: "category",
      title: recentCategory.label,
      titleIcon: QUICK_TYPE_ICONS.category,
      description: "Recent",
      ariaLabel: `${recentCategory.label}, recent category`,
      onClick: () => applyCategoryFilter(recentCategory.label),
    });
  }
  if (recentProduct?.id) {
    quickPickTiles.push({
      key: `recent-prod-${recentProduct.id}`,
      kind: "product",
      title: recentProduct.title,
      titleIcon: QUICK_TYPE_ICONS.item,
      description: "Recent",
      ariaLabel: `${recentProduct.title}, recent item`,
      onClick: () => {
        setSelectedCategory("");
        setSearchQuery(recentProduct.title);
      },
    });
  }

  const showQuickRow = props.includeCustom && quickPickTiles.length > 0;

  const quickGridItems = quickPickTiles.map((tile) => (
    <OptionGridCell
      key={tile.key}
      gridKey={tile.key}
      itemSx={{ display: { xs: "none", sm: "flex" } }}
    >
      <Option
        title={truncateQuickLabel(tile.title)}
        titleIcon={tile.titleIcon}
        description={tile.description}
        aria-label={tile.ariaLabel}
        onClick={tile.onClick}
        sx={QUICK_TILE_SX}
      />
    </OptionGridCell>
  ));

  const categoryGridItems =
    categoryMap && typeof categoryMap === "object"
      ? Object.entries(categoryMap)
          .sort(([k1], [k2]) => k1.trim().localeCompare(k2.trim()))
          .map(([cat, arr]) => (
            <OptionGridCell key={cat} gridKey={cat}>
              <Option
                title={capitalize(cat)}
                itemCount={arr.length}
                onClick={() => applyCategoryFilter(cat)}
                sx={OPTION_GRID_TILE_SX}
              />
            </OptionGridCell>
          ))
      : null;

  return (
    <Box sx={CONTAIN}>
      {products.length > 0 && (
        <Box sx={CONTAIN}>
          <Box
            sx={{
              ...CONTAIN,
              position: "sticky",
              top: 0,
              zIndex: 1000,
              backgroundColor: "#F7F7F7",
              pb: 1,
            }}
          >
            <Grid
              container
              columnSpacing={{ xs: 1, sm: GRID_COLUMN_SPACING }}
              rowSpacing={{ xs: 1, sm: 0 }}
              sx={{ marginTop: 0, alignItems: "stretch" }}
            >
              <Grid item xs={2} sm={2} md={3} sx={{ minWidth: 0 }}>
                <Button
                  variant="outlined"
                  color="secondary"
                  aria-label="Home — show all categories"
                  onClick={clearFilters}
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    px: 0,
                    height: 56,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <HomeOutlinedIcon sx={{ fontSize: "32px" }} />
                </Button>
              </Grid>
              <Grid item xs={10} sm={10} md={6} sx={{ minWidth: 0 }}>
                <TextField
                  id="query"
                  value={searchQuery}
                  label=""
                  name="query"
                  variant="outlined"
                  fullWidth
                  onChange={(e) => {
                    setSelectedCategory("");
                    setSearchQuery(e.target.value);
                  }}
                  placeholder="Search category & items"
                  sx={{
                    width: "100%",
                    minWidth: 0,
                    maxWidth: "100%",
                    "& .MuiOutlinedInput-root": {
                      minHeight: 56,
                      minWidth: 0,
                      alignItems: "center",
                      boxSizing: "border-box",
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment sx={{ paddingRight: "10px" }}>
                        <SearchOutlined />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment>
                        {searchQuery && (
                          <IconButton onClick={() => setSearchQuery("")} aria-label="Clear search">
                            <CloseOutlinedIcon />
                          </IconButton>
                        )}
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              {props.includeCustom && (
                <Grid
                  item
                  xs={12}
                  md={3}
                  sx={{
                    minWidth: 0,
                    display: "flex",
                    alignItems: "stretch",
                  }}
                >
                  <Box sx={{ width: "100%", minWidth: 0, "& .MuiButton-root": { width: "100%" } }}>
                    <CustomItem onChange={handleItemChange} />
                  </Box>
                </Grid>
              )}
            </Grid>
            {selectedCategory && (
              <Box pt={2} px={{ xs: 0, sm: 0.5 }}>
                <Breadcrumbs aria-label="Category path">
                  <Link
                    component="button"
                    type="button"
                    underline="hover"
                    color="inherit"
                    onClick={() => setSelectedCategory("")}
                    sx={{
                      cursor: "pointer",
                      border: 0,
                      background: "none",
                      font: "inherit",
                      p: 0,
                      typography: "body2",
                    }}
                  >
                    Home
                  </Link>
                  <Typography color="text.primary" variant="body2">
                    {capitalize(selectedCategory)} ({filteredProducts.length})
                  </Typography>
                </Breadcrumbs>
              </Box>
            )}
          </Box>
          <Box id="product-list" sx={{ scrollMarginTop: "150px" }} />
          {!selectedCategory && !searchQuery && (
            <Box sx={CONTAIN}>
              {showQuickRow && (
                <Grid
                  container
                  columnSpacing={GRID_COLUMN_SPACING}
                  rowSpacing={GRID_ROW_SPACING}
                  role="group"
                  aria-label="Popular and recent"
                  sx={{ ...catalogGridContainerSx, mb: 2 }}
                >
                  {quickGridItems}
                </Grid>
              )}
              <Grid
                container
                columnSpacing={GRID_COLUMN_SPACING}
                rowSpacing={GRID_ROW_SPACING}
                role="group"
                aria-label="Catalog"
                sx={catalogGridContainerSx}
              >
                {categoryGridItems}
              </Grid>
            </Box>
          )}
          {(selectedCategory || searchQuery) && (
            <Box sx={CONTAIN}>
              {searchQuery && !selectedCategory && filteredCategories.length > 0 && (
                <Grid
                  container
                  columnSpacing={GRID_COLUMN_SPACING}
                  rowSpacing={GRID_ROW_SPACING}
                  role="group"
                  aria-label="Matching categories"
                  sx={{ ...catalogGridContainerSx, mb: 2 }}
                >
                  {filteredCategories.map(([cat, arr]) => (
                    <OptionGridCell key={cat} gridKey={`search-cat-${cat}`}>
                      <Option
                        title={capitalize(cat)}
                        itemCount={arr.length}
                        onClick={() => applyCategoryFilter(cat)}
                        sx={OPTION_GRID_TILE_SX}
                      />
                    </OptionGridCell>
                  ))}
                </Grid>
              )}
              <Grid
                container
                columnSpacing={GRID_COLUMN_SPACING}
                rowSpacing={GRID_ROW_SPACING}
                role="group"
                aria-label="Matching items"
                sx={{ ...catalogGridContainerSx, mb: 2 }}
              >
                {filteredProducts.map((o) => (
                  <OptionGridCell key={o.id ?? o.productId} gridKey={o.id ?? o.productId}>
                    <StandardItem product={o} fillGridCell onChange={handleItemChange} />
                  </OptionGridCell>
                ))}
              </Grid>
            </Box>
          )}
        </Box>
      )}
      {!loading && products.length === 0 && (
        <Container maxWidth="sm">
          <Box py={4} textAlign={"center"}>
            <Typography variant="h5" color="error" gutterBottom>
              No Active Items Found!
            </Typography>
            <Typography variant="body1" color="textSecondary" gutterBottom>
              Click the button below to view your Catalog & add one or more items. Add everyday,
              seasonal & limited-time items to make it easier to order.
            </Typography>
            <Box my={3}>
              <Button variant="contained" component={RouterLink} to={`/shop`}>
                View Catalog
              </Button>
            </Box>
          </Box>
        </Container>
      )}
    </Box>
  );
}
